import React from 'react';
import AsyncSelect from 'react-select/async';
import { Icon } from '@lomarengas/design-system';
import { useAppDispatch } from '../../../app/hooks';
import {
  AccommodationAutoCompleteOption,
  getAccommodationAutoCompleteOptionsAsync,
} from '../../../features/accommodations/accommodationsSlice';

interface AccommodationSelectorProps {
  Placeholder?: string;
  Subtitle: string;
  Title: string;
  onAccommodationSelected?: (selectedAccommodationId: string) => void;
}

interface OptionType {
  value: string;
  label: string;
}

const AccommodationSelector = ({
  Placeholder = 'Hae kohteen numerolla tai nimellä...',
  Subtitle,
  Title,
  onAccommodationSelected,
}: AccommodationSelectorProps) => {
  const dispatch = useAppDispatch();

  const handleSelectAccommodationChange = (selectedOption: OptionType) => {
    if (selectedOption && selectedOption.label && selectedOption.value) {
      const accommodationId = selectedOption.value;
      onAccommodationSelected(accommodationId);
    } else {
      onAccommodationSelected(null);
    }
  };

  // Transform fetched options to match react-select format
  const formatOptions = (
    options: AccommodationAutoCompleteOption[] | undefined,
  ): OptionType[] => {
    if (!options || options.length === 0) {
      return [];
    }

    const newOptions = options.map(option => ({
      value: option.accommodationId,
      label: `${option.accommodationNumber} - ${option.name}`,
    }));
    return newOptions;
  };

  const loadOptions = async (inputValue: string): Promise<OptionType[]> => {
    try {
      const response = await dispatch(
        getAccommodationAutoCompleteOptionsAsync(inputValue),
      );
      // Access response data and format the options
      return formatOptions(response.payload.data);
    } catch (error) {
      console.error('Error fetching options:', error);
      return [];
    }
  };

  return (
    <>
      <div className="d-flex align-items-start align-items-sm-center mb-24 mb-xl-32">
        <div className="d-block bg-primary fs-3 me-24 px-16 py-8 rounded text-center text-white">
          <Icon Name="house-fill" />
        </div>
        <div className="flex-grow-1">
          <h2 className="h5 mb-0">{Title}</h2>
          <p className="text-gray-600 mb-0">{Subtitle}</p>
        </div>
      </div>
      <AsyncSelect
        styles={{
          control: baseStyles => ({
            ...baseStyles,
            borderColor: '#ced4da',
            borderRadius: 6,
          }),
          dropdownIndicator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
          indicatorSeparator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
        }}
        classNames={{
          control: state => 'form-control form-control-search ps-48 pe-8',
        }}
        inputId="accommodationId"
        name="accommodationId"
        placeholder={Placeholder}
        isClearable
        isSearchable
        loadOptions={loadOptions}
        cacheOptions
        onChange={option => handleSelectAccommodationChange(option)}
      />
    </>
  );
};

export default AccommodationSelector;
export { AccommodationSelector };
export type { AccommodationSelectorProps };
